<template>
  <div v-if="isVisible" class="pacc-overlay" @click.self="close" role="dialog" aria-modal="true" aria-labelledby="pacc-title" @keydown.esc="close">

    <div class="pacc-dialog">

      <!-- ═══ HEADER ═══ -->
      <div class="pacc-head">
        <div class="pacc-head-left">
          <div class="pacc-head-icon"><i class="ri-add-circle-line"></i></div>
          <div>
            <div id="pacc-title" class="pacc-head-title">Sélectionnez un accompagnement</div>
            <div class="pacc-head-sub">
              {{ accompagnements.length }} option{{ accompagnements.length > 1 ? 's' : '' }} disponible{{ accompagnements.length > 1 ? 's' : '' }}
            </div>
          </div>
        </div>
        <button class="pacc-close" @click="close" aria-label="Fermer la modale">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <!-- ═══ RECHERCHE (si > 5 options) ═══ -->
      <div v-if="accompagnements.length > 5" class="pacc-search-bar">
        <div class="pacc-search-wrap">
          <i class="ri-search-line pacc-search-icon"></i>
          <input
            v-model="searchQuery"
            type="search"
            class="pacc-search"
            placeholder="Rechercher un accompagnement..."
            @keyup.enter="selectFirstResult"
          />
          <button v-if="searchQuery" @click="searchQuery = ''" class="pacc-search-clear" aria-label="Effacer">
            <i class="ri-close-line"></i>
          </button>
        </div>
      </div>

      <!-- ═══ BANDEAU ARTICLE ═══ -->
      <div v-if="currentItem && currentItem.name" class="pacc-banner">
        <div class="pacc-banner-icon"><i class="ri-shopping-bag-line"></i></div>
        <div>
          <div class="pacc-banner-label">Article sélectionné</div>
          <div class="pacc-banner-name">{{ currentItem.name }}</div>
        </div>
      </div>

      <!-- ═══ BODY (scrollable) ═══ -->
      <div class="pacc-body">

        <template v-if="filteredAccompagnements.length">
          <button
            v-for="(item, index) in filteredAccompagnements"
            :key="item.pid || item.id || index"
            @dblclick="selectAccompagnement(item)"
            :class="['pacc-item', { 'pacc-item-active': selectedIndex === index }]"
          >
            <div :class="['pacc-item-num', { 'pacc-item-num-active': selectedIndex === index }]">
              {{ index + 1 }}
            </div>
            <div class="pacc-item-info">
              <div class="pacc-item-name">{{ item.name }}</div>
              <div v-if="item.description" class="pacc-item-desc">{{ item.description }}</div>
            </div>
            <div v-if="item.prix || item.price" class="pacc-item-price">
              {{ formatPrice(item.prix || item.price) }}
            </div>
            <i class="ri-arrow-right-s-line pacc-item-arrow"></i>
          </button>
        </template>

        <!-- Empty state -->
        <div v-else class="pacc-empty">
          <i class="ri-search-line pacc-empty-icon"></i>
          <p>{{ searchQuery ? 'Aucun résultat' : 'Aucun accompagnement disponible' }}</p>
          <small v-if="searchQuery">Essayez de modifier votre recherche</small>
        </div>

      </div>

      <!-- ═══ FOOTER ═══ -->
      <div class="pacc-foot">
        <p class="pacc-foot-hint">
          <kbd class="pacc-kbd">Double-clic</kbd> ou <kbd class="pacc-kbd">Entrée</kbd> pour sélectionner
        </p>
        <button class="pacc-btn-sec" @click="close">Annuler</button>
      </div>

    </div>
  </div>
</template>

<script>
    import { currencyFormatDE } from '@/allfunction';

    export default {
        name: 'AccompanimentSelectionModal',

        emits: ['selectaccompagnement', 'closed'],

        data() {
            return {
                // Visibility
                isVisible: false,

                // Data
                accompagnements: [],
                currentItem: null,
                checkPrice: 0,
                dataPrices: [],

                // Search & Selection
                searchQuery: '',
                selectedIndex: -1,
            };
        },

        computed: {
            filteredAccompagnements() {
                if (!this.searchQuery.trim()) {
                    return this.accompagnements;
                }

                const query = this.searchQuery.toLowerCase().trim();
                return this.accompagnements.filter(item => {
                    const name = item.name?.toLowerCase() || '';
                    const description = item.description?.toLowerCase() || '';
                    return name.includes(query) || description.includes(query);
                });
            },
        },

        watch: {
            searchQuery() {
                this.selectedIndex = -1;
            },
        },

        methods: {
            // ============ MODAL METHODS ============
            show(accompagnements, item, checkPrice = 0, dataPrices = []) {
                this.accompagnements = accompagnements || [];
                this.currentItem = item || null;
                this.checkPrice = checkPrice;
                this.dataPrices = dataPrices || [];
                this.searchQuery = '';
                this.selectedIndex = -1;
                this.isVisible = true;

                document.body.style.overflow = 'hidden';
                document.addEventListener('keydown', this.handleKeydown);
            },

            close() {
                this.isVisible = false;
                document.body.style.overflow = '';
                document.removeEventListener('keydown', this.handleKeydown);
            },

            // ============ SELECTION METHODS ============
            selectAccompagnement(accompagnement) {
                this.$emit('selectaccompagnement', {
                    accompagnement,
                    item: this.currentItem,
                    checkprice: this.checkPrice,
                    dataprice: this.dataPrices,
                });
                this.close();
            },

            selectFirstResult() {
                if (this.filteredAccompagnements.length > 0) {
                    const index = this.selectedIndex >= 0 ? this.selectedIndex : 0;
                    this.selectAccompagnement(this.filteredAccompagnements[index]);
                }
            },

            skipSelection() {
                this.$emit('selectaccompagnement', {
                    accompagnement: null,
                    item: this.currentItem,
                    checkprice: this.checkPrice,
                    dataprice: this.dataPrices,
                });
                this.close();
            },

            // ============ KEYBOARD NAVIGATION ============
            handleKeydown(event) {
                if (!this.isVisible) return;

                switch (event.key) {
                    case 'Escape':
                        this.close();
                        break;
                    case 'ArrowDown':
                        event.preventDefault();
                        this.selectedIndex = Math.min(
                            this.selectedIndex + 1,
                            this.filteredAccompagnements.length - 1
                        );
                        this.scrollToSelected();
                        break;
                    case 'ArrowUp':
                        event.preventDefault();
                        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
                        this.scrollToSelected();
                        break;
                    case 'Enter':
                        if (this.selectedIndex >= 0 && this.filteredAccompagnements[this.selectedIndex]) {
                            this.selectAccompagnement(this.filteredAccompagnements[this.selectedIndex]);
                        } else if (this.filteredAccompagnements.length === 1) {
                            this.selectAccompagnement(this.filteredAccompagnements[0]);
                        }
                        break;
                }
            },

            scrollToSelected() {
                this.$nextTick(() => {
                    const selected = this.$el?.querySelector('.pacc-item-active');
                    selected?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                });
            },

            // ============ UTILITIES ============
            formatPrice(value) {
                if (value === null || value === undefined) return '';
                return currencyFormatDE(value);
            },
        },

        beforeUnmount() {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', this.handleKeydown);
        },
    };
</script>

<style scoped>
/* ── Overlay ── */
.pacc-overlay {
  position: fixed; inset: 0; z-index: 10030;
  background: rgba(0, 0, 0, .55);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  font-family: 'Segoe UI', system-ui, sans-serif;
}

/* ── Dialog ── */
.pacc-dialog {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 14px; width: min(480px, 100%);
  max-height: 90dvh; display: flex; flex-direction: column;
  overflow: hidden; box-shadow: 0 24px 60px rgba(0, 0, 0, .45);
  animation: pacc-in .22s cubic-bezier(.4, 0, .2, 1);
}
@keyframes pacc-in {
  from { opacity: 0; transform: scale(.94) translateY(10px); }
  to   { opacity: 1; transform: none; }
}

/* ── Header ── */
.pacc-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.pacc-head-left { display: flex; align-items: center; gap: 10px; }
.pacc-head-icon {
  width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  background: color-mix(in srgb, var(--purple) 15%, transparent);
  color: var(--purple); font-size: 18px;
  display: flex; align-items: center; justify-content: center;
}
.pacc-head-title { font-size: 14px; font-weight: 800; color: var(--text); }
.pacc-head-sub   { font-size: 11px; color: var(--text2); margin-top: 1px; }
.pacc-close {
  background: none; border: none; color: var(--text2); font-size: 20px;
  cursor: pointer; line-height: 1; padding: 4px; border-radius: 6px;
  flex-shrink: 0; transition: color .12s;
}
.pacc-close:hover { color: var(--red); }

/* ── Search bar ── */
.pacc-search-bar { padding: 0 14px 12px; flex-shrink: 0; }
.pacc-search-wrap { position: relative; }
.pacc-search-icon {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  color: var(--text3); pointer-events: none; font-size: 15px;
}
.pacc-search {
  width: 100%; padding: 9px 34px 9px 32px;
  background: var(--surface2); border: 1px solid var(--border);
  border-radius: 8px; color: var(--text); font-size: 13px;
  outline: none; font-family: inherit; transition: border-color .15s;
}
.pacc-search:focus { border-color: var(--purple); }
.pacc-search::placeholder { color: var(--text3); }
.pacc-search-clear {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: var(--text2); cursor: pointer;
  font-size: 14px; padding: 0;
}

/* ── Bandeau article ── */
.pacc-banner {
  display: flex; align-items: center; gap: 10px;
  margin: 0 14px 10px; padding: 10px 12px;
  background: color-mix(in srgb, var(--purple) 8%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--purple) 30%, var(--border));
  border-radius: 10px; flex-shrink: 0;
}
.pacc-banner-icon {
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  background: color-mix(in srgb, var(--purple) 20%, transparent);
  color: var(--purple); font-size: 15px;
  display: flex; align-items: center; justify-content: center;
}
.pacc-banner-label {
  font-size: 10px; font-weight: 700; color: var(--purple);
  text-transform: uppercase; letter-spacing: .4px;
}
.pacc-banner-name { font-size: 13px; font-weight: 700; color: var(--text); margin-top: 1px; }

/* ── Body scrollable ── */
.pacc-body {
  flex: 1; overflow-y: auto; padding: 10px 14px;
  display: flex; flex-direction: column; gap: 6px;
  scrollbar-width: thin; scrollbar-color: var(--border) transparent;
}
.pacc-body::-webkit-scrollbar { width: 4px; }
.pacc-body::-webkit-scrollbar-track { background: transparent; }
.pacc-body::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }

/* ── Items ── */
.pacc-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px; border-radius: 10px;
  background: var(--surface2); border: 1px solid var(--border);
  cursor: pointer; text-align: left; width: 100%; font-family: inherit;
  color: var(--text); transition: border-color .1s, background .1s;
}
.pacc-item:hover {
  border-color: var(--purple);
  background: color-mix(in srgb, var(--purple) 6%, var(--surface2));
}
.pacc-item-active {
  border-color: var(--purple) !important;
  background: color-mix(in srgb, var(--purple) 12%, var(--surface2)) !important;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--purple) 30%, transparent);
}
.pacc-item-num {
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  background: var(--border); color: var(--text2);
  font-size: 12px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  transition: background .1s, color .1s;
}
.pacc-item:hover .pacc-item-num {
  background: color-mix(in srgb, var(--purple) 20%, transparent);
  color: var(--purple);
}
.pacc-item-num-active { background: var(--purple) !important; color: #fff !important; }
.pacc-item-info { flex: 1; min-width: 0; }
.pacc-item-name { font-size: 13px; font-weight: 700; color: var(--text); }
.pacc-item-desc {
  font-size: 11px; color: var(--text2); margin-top: 2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pacc-item-price {
  font-size: 12px; font-weight: 800; padding: 3px 8px; border-radius: 20px;
  flex-shrink: 0;
  background: color-mix(in srgb, var(--green) 15%, transparent);
  color: var(--green);
}
.pacc-item-arrow { font-size: 16px; color: var(--text3); flex-shrink: 0; transition: color .1s; }
.pacc-item:hover .pacc-item-arrow { color: var(--purple); }

/* ── Empty state ── */
.pacc-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 48px 20px; text-align: center;
}
.pacc-empty-icon { font-size: 38px; color: var(--text3); margin-bottom: 12px; }
.pacc-empty p    { font-size: 14px; font-weight: 600; color: var(--text2); }
.pacc-empty small { font-size: 11px; color: var(--text3); margin-top: 4px; display: block; }

/* ── Footer ── */
.pacc-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 12px 14px; border-top: 1px solid var(--border); flex-shrink: 0;
  background: color-mix(in srgb, var(--surface2) 50%, var(--surface));
}
.pacc-foot-hint { font-size: 11px; color: var(--text3); }
.pacc-kbd {
  display: inline-block; padding: 2px 6px; border-radius: 4px;
  background: var(--surface2); border: 1px solid var(--border);
  font-size: 10px; font-weight: 700; color: var(--text2);
  font-family: ui-monospace, monospace;
}
.pacc-btn-sec {
  padding: 8px 18px; border-radius: 8px; flex-shrink: 0;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text2); font-size: 13px; font-weight: 600;
  cursor: pointer; font-family: inherit; transition: all .12s;
}
.pacc-btn-sec:hover { border-color: var(--red); color: var(--red); }

/* ── Mobile bottom-sheet ── */
@media (max-width: 640px) {
  .pacc-overlay { align-items: flex-end; padding: 0; }
  .pacc-dialog {
    width: 100%; max-height: 92dvh;
    border-radius: 16px 16px 0 0;
    animation: pacc-slide-up .25s cubic-bezier(.4, 0, .2, 1);
  }
  .pacc-foot-hint { display: none; }
}
@keyframes pacc-slide-up {
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
</style>
